// HTTP client. Session = short JWT from POST /auth/telegram (initData → token).
// The token is refreshed by re-authenticating with the current initData, never by trusting
// anything the client holds. Only *types* come from @fonus/shared, so zod stays out of the bundle.
import type { ApiError, ApiErrorCode, AuthResponse } from '@fonus/shared';
import { API_URL } from '../env.ts';
import { rawInitData } from '../telegram/tg.ts';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode | 'NETWORK',
    readonly body?: unknown,
  ) {
    super(`${status} ${code}`);
  }
}

interface Session {
  token: string;
  expiresAt: number;
}

let session: Session | null = null;
let pendingAuth: Promise<AuthResponse> | null = null;
const authListeners = new Set<(auth: AuthResponse) => void>();
const fatalListeners = new Set<(code: 'INIT_DATA_EXPIRED') => void>();

/** Refresh a little before expiry so a request never races the 15-minute boundary. */
const REFRESH_MARGIN_MS = 60_000;

async function parseError(res: Response): Promise<ApiRequestError> {
  const body = (await res.json().catch(() => undefined)) as ApiError | undefined;
  return new ApiRequestError(res.status, body?.error ?? 'INTERNAL', body);
}

async function doAuthenticate(): Promise<AuthResponse> {
  const initData = rawInitData();
  if (!initData) throw new ApiRequestError(401, 'UNAUTHORIZED');
  let res: Response;
  try {
    res = await fetch(`${API_URL}/auth/telegram`, {
      method: 'POST',
      headers: { authorization: `tma ${initData}` },
    });
  } catch {
    throw new ApiRequestError(0, 'NETWORK');
  }
  if (!res.ok) {
    const err = await parseError(res);
    if (err.code === 'INIT_DATA_EXPIRED') fatalListeners.forEach((l) => l('INIT_DATA_EXPIRED'));
    throw err;
  }
  const auth = (await res.json()) as AuthResponse;
  session = { token: auth.token, expiresAt: Date.parse(auth.expiresAt) };
  authListeners.forEach((l) => l(auth));
  return auth;
}

/** Single-flight: parallel callers share one /auth/telegram request. */
export function authenticate(): Promise<AuthResponse> {
  pendingAuth ??= doAuthenticate().finally(() => {
    pendingAuth = null;
  });
  return pendingAuth;
}

export function onAuthenticated(listener: (auth: AuthResponse) => void): () => void {
  authListeners.add(listener);
  return () => authListeners.delete(listener);
}

/** initData older than 24 h — the app must be reopened from the bot. */
export function onFatalAuth(listener: (code: 'INIT_DATA_EXPIRED') => void): () => void {
  fatalListeners.add(listener);
  return () => fatalListeners.delete(listener);
}

async function token(): Promise<string> {
  if (!session || session.expiresAt - Date.now() < REFRESH_MARGIN_MS) await authenticate();
  // authenticate() always sets the session or throws.
  return session?.token ?? '';
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH';
  body?: unknown;
  signal?: AbortSignal;
  /** Keep the request alive while the page is being hidden/closed (listen heartbeats). */
  keepalive?: boolean;
}

/** Authenticated JSON request. Retries once with a fresh session on 401. 204 → null. */
export async function api<T>(path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
  const bearer = await token();
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: opts.method ?? 'GET',
      headers: {
        authorization: `Bearer ${bearer}`,
        ...(opts.body !== undefined && { 'content-type': 'application/json' }),
      },
      ...(opts.body !== undefined && { body: JSON.stringify(opts.body) }),
      ...(opts.signal && { signal: opts.signal }),
      ...(opts.keepalive && { keepalive: true }),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiRequestError(0, 'NETWORK');
  }
  if (res.status === 401 && !retried) {
    session = null;
    return api<T>(path, opts, true);
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return null as T;
  return (await res.json()) as T;
}

/** Test hook. */
export function resetSessionForTests(): void {
  session = null;
  pendingAuth = null;
}
